import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import type { components } from "@/api/schema";
import { RequestError } from "@/app/queries";

/** Карта когорты (M4) и аналитика (A3) — SP5. Единица прогресса — сданный гейт. */
export type CohortMap = components["schemas"]["CohortMap"];
export type MapRow = components["schemas"]["MapRow"];
export type MapStep = components["schemas"]["MapStep"];
export type AnalyticsReport = components["schemas"]["AnalyticsReport"];
export type FunnelRow = components["schemas"]["FunnelRow"];
export type MentorStat = components["schemas"]["MentorStat"];
export type Period = "all" | "4w" | "12w";

function fail(status: number, error: unknown): never {
  const e = error as { error?: string; message?: string } | undefined;
  throw new RequestError(status, e?.error ?? "unknown", e?.message ?? "Не удалось выполнить запрос.");
}

const noRetry4xx = (n: number, err: unknown) => !(err instanceof RequestError && err.status >= 400 && err.status < 500) && n < 2;

export const analyticsKeys = {
  map: (scope: string) => ["cohort-map", scope] as const,
  report: (scope: string, period: Period) => ["analytics", scope, period] as const,
};

export function useCohortMap(scope: string) {
  return useQuery({
    queryKey: analyticsKeys.map(scope),
    queryFn: async () => {
      const { data, error, response } = await api.GET("/mentor/cohorts/{scope}/map", { params: { path: { scope } } });
      if (!data) fail(response.status, error);
      return data;
    },
    retry: noRetry4xx,
  });
}

export function useAnalytics(scope: string, period: Period = "all") {
  return useQuery({
    queryKey: analyticsKeys.report(scope, period),
    queryFn: async () => {
      const { data, error, response } = await api.GET("/admin/analytics/{scope}", { params: { path: { scope }, query: { period } } });
      if (!data) fail(response.status, error);
      return data;
    },
    retry: noRetry4xx,
  });
}

/** «пересчитать» — ставит задачу; отчёт обновится после пересчёта. */
export function useRecompute(scope: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { error, response } = await api.POST("/admin/analytics/{scope}/recompute", { params: { path: { scope } } });
      if (!response.ok) fail(response.status, error);
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["analytics"] }),
  });
}

/** Ссылка на выгрузку CSV (скачивание — обычный GET с cookie). */
export function funnelCSVHref(scope: string, period: Period = "all"): string {
  return `/api/admin/analytics/${encodeURIComponent(scope)}/funnel.csv?period=${period}`;
}
